import { GraduationCapIcon } from "lucide-react"
import { driver } from "driver.js"
import { useEffect, useRef } from "react"
import { buttonVariants } from "~/components/ui/button"
import type { tutorials } from "~/lib/tutorials"
import "./tutorials.css"

// Driver supplies the spotlight and positioning; React owns task completion.
export function OnboardingStep(props: {
	step: number
	task: (typeof tutorials)[
		| "onboarding-v1"
		| "queue-v1"
		| "projects-v1"]["steps"][number]
	total: number
	complete: boolean
	pending: boolean
	onBack: () => void
	onAdvance: (skipped: boolean) => void
}) {
	const marker = useRef<HTMLSpanElement>(null)
	const latest = useRef(props)
	latest.current = props
	const instance = useRef<ReturnType<typeof driver> | undefined>(undefined)
	const { step, task, total } = props
	const { target, goal, element: selector, popover } = task
	const { title, description } = popover
	const popoverClass =
		"popoverClass" in popover ? popover.popoverClass : "orbit-tutorial"
	useEffect(() => {
		const root = marker.current?.closest("main")
		if (!root) return
		// Driver normally traps Tab in the spotlight. Hands-on editing also needs
		// portalled menus, editor toolbars and the page's Save changes bar.
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Tab") event.stopImmediatePropagation()
		}
		window.addEventListener("keydown", onKeyDown)
		document.body.classList.add("orbit-onboarding-active")
		const tour = driver({
			animate: !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
			allowScroll: true,
			allowKeyboardControl: false,
			disableActiveInteraction: false,
			overlayClickBehavior: "none",
			overlayOpacity: 0.35,
			popoverClass,
			stageRadius: 8,
			onPopoverRender: (popover) => {
				// Clone the static React-rendered goal into Driver’s description.
				popover.description.textContent = description
				const goalContent = marker.current?.firstElementChild
				if (goalContent) popover.description.append(goalContent.cloneNode(true))
				const dialog = tour
					.getActiveElement()
					?.closest('[data-slot="sheet-content"]')
				if (dialog) {
					dialog.append(popover.wrapper)
					popover.wrapper.setAttribute("popover", "manual")
					popover.wrapper.showPopover()
				}
				popover.previousButton.className = `driver-popover-prev-btn ${buttonVariants({ variant: "ghost", size: "sm" })}`
				popover.closeButton.className = `driver-popover-close-btn ${buttonVariants({ variant: "ghost", size: "sm" })}`
				popover.nextButton.className = `driver-popover-next-btn ${buttonVariants({ size: "sm" })}`
				popover.closeButton.textContent = "Skip"
				popover.footerButtons.insertBefore(
					popover.closeButton,
					popover.nextButton
				)
				popover.previousButton.disabled = step === 1 || latest.current.pending
				popover.closeButton.disabled = latest.current.pending
				popover.nextButton.disabled =
					!latest.current.complete || latest.current.pending
				popover.progress.textContent = `${step} of ${total}${latest.current.complete ? " · Done" : ""}`
			},
			onNextClick: () => {
				if (!latest.current.complete || latest.current.pending) return
				tour
					.getActiveElement()
					?.closest('[data-slot="sheet-content"]')
					?.querySelector<HTMLButtonElement>('[data-slot="sheet-close"]')
					?.click()
				latest.current.onAdvance(false)
			},
			onPrevClick: () => {
				if (step === 1 || latest.current.pending) return
				tour
					.getActiveElement()
					?.closest('[data-slot="sheet-content"]')
					?.querySelector<HTMLButtonElement>('[data-slot="sheet-close"]')
					?.click()
				latest.current.onBack()
			},
			onCloseClick: () => {
				if (latest.current.pending) return
				tour
					.getActiveElement()
					?.closest('[data-slot="sheet-content"]')
					?.querySelector<HTMLButtonElement>('[data-slot="sheet-close"]')
					?.click()
				latest.current.onAdvance(true)
			}
		})
		instance.current = tour
		let selected: Element | undefined
		let frame = 0
		let resizeFrame = 0
		// Reply composers and rich editors expand inside the same target node.
		// Refresh its bounds without restarting the step or moving keyboard focus.
		const resizeObserver = new ResizeObserver(() => {
			cancelAnimationFrame(resizeFrame)
			resizeFrame = requestAnimationFrame(() => {
				// Adding resource inputs grows the target after Driver has scrolled it into
				// view. Keep that target above the fixed save bar, then measure it again.
				if (target === "project-resources" && selected) {
					let scroller = selected.parentElement
					while (
						scroller &&
						!/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)
					)
						scroller = scroller.parentElement
					if (scroller) {
						const saveBar = root.querySelector<HTMLElement>(
							"[data-onboarding-support]"
						)
						const clearance = Math.max(128, (saveBar?.offsetHeight ?? 0) + 16)
						const overflow =
							selected.getBoundingClientRect().bottom -
							(scroller.getBoundingClientRect().bottom - clearance)
						if (overflow > 0) scroller.scrollTop += overflow
					}
				}
				tour.refresh()
			})
		})
		const highlight = () => {
			const candidates =
				target === "properties"
					? document.querySelectorAll(selector)
					: root.querySelectorAll(selector)
			const element =
				[...candidates].find((node) => node.getClientRects().length > 0) ??
				(target === "properties"
					? root.querySelector('[aria-label="Issue details"]')
					: undefined)
			if (!element || element === selected) return
			resizeObserver.disconnect()
			selected = element
			resizeObserver.observe(element)
			tour.highlight({
				element,
				popover: {
					title,
					description,
					side: popover.side,
					align: popover.align,
					showButtons: ["previous", "next", "close"],
					showProgress: true,
					progressText: `${step} of ${total}`,
					prevBtnText: "Back",
					nextBtnText: step === total ? "Finish tour" : "Continue",
					closeBtnLabel: "Skip"
				}
			})
		}
		const observer = new MutationObserver(() => {
			cancelAnimationFrame(frame)
			frame = requestAnimationFrame(highlight)
		})
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ["data-state", "hidden"]
		})
		window.addEventListener("resize", highlight)
		highlight()
		return () => {
			observer.disconnect()
			resizeObserver.disconnect()
			cancelAnimationFrame(resizeFrame)
			cancelAnimationFrame(frame)
			window.removeEventListener("resize", highlight)
			window.removeEventListener("keydown", onKeyDown)
			tour.destroy()
			instance.current = undefined
			document.body.classList.remove("orbit-onboarding-active")
		}
	}, [
		step,
		target,
		title,
		description,
		goal,
		selector,
		popoverClass,
		popover.side,
		popover.align,
		total
	])
	useEffect(() => {
		const popover = instance.current?.getState().popover
		if (!popover) return
		popover.nextButton.disabled = !props.complete || props.pending
		popover.previousButton.disabled = step === 1 || props.pending
		popover.closeButton.disabled = props.pending
		popover.progress.textContent = `${step} of ${total}${props.complete ? " · Done" : ""}`
	}, [props.complete, props.pending, step, total])
	return (
		<span ref={marker} hidden>
			<span className="mt-4 flex items-start gap-2 text-text-main">
				<GraduationCapIcon
					aria-hidden="true"
					className="mt-0.5 size-4 shrink-0 text-primary"
				/>
				<span>
					<strong>Goal:</strong> {goal}
				</span>
			</span>
		</span>
	)
}
